import React from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  DatePicker,
  Divider,
  Empty,
  Form,
  Image,
  Input,
  Input as AntInput,
  InputNumber,
  List,
  message,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Upload
} from 'antd';
import type { UploadProps } from 'antd';
import {
  CloudUploadOutlined,
  DeleteOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PictureOutlined,
  PlusCircleOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ru';

import { combosAPI, menuAPI, suppliersAPI, API_BASE_URL } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { useIsMobile } from '../hooks/useIsMobile';

dayjs.locale('ru');

type DayPlannerTab = 'catalog' | 'planner' | 'combos';

type PlannerMap = Record<string, PlannedMenuItem[]>;

interface PlannedMenuItem {
  id?: number;
  menuItemId: number;
  menuItem?: any;
  specificDate?: string | null;
  dayOfWeek?: string | null;
  availableQuantity?: number;
  isAvailable?: boolean;
}

type ComboPricingMode = 'fixed' | 'sum_items';

type ComboGroupDraft = {
  title: string;
  requiredCount: number;
  menuItemIds: number[];
};

type ComboDraft = {
  id?: number;
  title: string;
  description?: string | null;
  allowCustomization: boolean;
  pricingMode: ComboPricingMode;
  fixedPrice?: number | null;
  isActive: boolean;
  groups: ComboGroupDraft[];
};

const categoryStyles: Record<string, { label: string; color: string }> = {
  soup: { label: 'Супы', color: '#5B8FF9' },
  main_course: { label: 'Основные блюда', color: '#5AD8A6' },
  side_dish: { label: 'Гарниры', color: '#CDDDFD' },
  salad: { label: 'Салаты', color: '#F6BD16' },
  snack: { label: 'Закуски', color: '#9270CA' },
  breakfast: { label: 'Завтраки', color: '#FF9D4D' },
  bakery: { label: 'Выпечка', color: '#F08C00' },
  dessert: { label: 'Десерты', color: '#E86452' },
  beverage: { label: 'Напитки', color: '#6DC8EC' },
  other: { label: 'Прочее', color: '#8C8C8C' }
};

const categoryOrder: Record<string, number> = {
  soup: 1,
  main_course: 2,
  side_dish: 3,
  salad: 4,
  snack: 5,
  breakfast: 6,
  bakery: 7,
  dessert: 8,
  beverage: 9,
  other: 10
};

const attributeChips = [
  { key: 'isVegan', color: 'green', label: '🌱 Веган' },
  { key: 'isLenten', color: 'blue', label: '⛪ Постное' },
  { key: 'isHalal', color: 'orange', label: '☪️ Халяль' }
] as const;

const availabilityConfig = {
  true: { color: 'success', text: 'В наличии' },
  false: { color: 'error', text: 'Нет в наличии' }
} as const;

const { Text } = Typography;
const { RangePicker } = DatePicker;

const formatCurrency = (value?: number) =>
  typeof value === 'number'
    ? value.toLocaleString('ru-RU', {
        style: 'currency',
        currency: 'RUB',
        maximumFractionDigits: 0
      })
    : '—';

const formatGrams = (value?: number) =>
  typeof value === 'number' ? `${value.toLocaleString('ru-RU')} г` : '—';

const formatCalories = (value?: number) =>
  typeof value === 'number' ? `${value.toLocaleString('ru-RU')} ккал` : '—';

const StatisticCard: React.FC<{ title: string; value: number }> = ({ title, value }) => (
  <Card
    size="small"
    style={{
      minWidth: 200,
      background: '#fafafa',
      borderRadius: 12,
      boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
      border: '1px solid #f0f0f0'
    }}
    bodyStyle={{ padding: '16px 20px' }}
  >
    <Text type="secondary" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>
      {title}
    </Text>
    <Text style={{ fontSize: 24, fontWeight: 600 }}>{value}</Text>
  </Card>
);

const MyMenu: React.FC = () => {
  const { user } = useAuthStore();
  const isMobile = useIsMobile();

  const [loading, setLoading] = React.useState(false);
  const [items, setItems] = React.useState<any[]>([]);
  const [createVisible, setCreateVisible] = React.useState(false);
  const [editVisible, setEditVisible] = React.useState(false);
  const [current, setCurrent] = React.useState<any>(null);
  const [imageModalVisible, setImageModalVisible] = React.useState(false);
  const [imageItem, setImageItem] = React.useState<any>(null);
  const [uploadingImage, setUploadingImage] = React.useState(false);
  const [uploadingExcel, setUploadingExcel] = React.useState(false);
  const [uploadingPlannerExcel, setUploadingPlannerExcel] = React.useState(false);
  const [form] = Form.useForm();

  const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = React.useState(false);
  const [bulkDeleteConfirmText, setBulkDeleteConfirmText] = React.useState('');
  const [duplicatesOpen, setDuplicatesOpen] = React.useState(false);
  const [duplicatesLoading, setDuplicatesLoading] = React.useState(false);
  const [duplicatesRemoving, setDuplicatesRemoving] = React.useState(false);
  const [duplicateReport, setDuplicateReport] = React.useState<{
    groupCount: number;
    duplicateCount: number;
    groups: Array<{
      key: string;
      keepId: number;
      duplicateIds: number[];
      items: Array<{
        id: number;
        name: string;
        category: string;
        price: number;
        isAvailable: boolean;
      }>;
    }>;
  } | null>(null);

  const [catalogFilterCategories, setCatalogFilterCategories] = React.useState<string[]>([]);
  const [catalogFilterAvailable, setCatalogFilterAvailable] = React.useState<'all' | 'available' | 'unavailable'>(
    'all'
  );
  const [catalogFilterFlags, setCatalogFilterFlags] = React.useState<Array<'isVegan' | 'isLenten' | 'isHalal'>>([]);
  const [catalogPriceMin, setCatalogPriceMin] = React.useState<number | null>(null);
  const [catalogPriceMax, setCatalogPriceMax] = React.useState<number | null>(null);

  const [activeTab, setActiveTab] = React.useState<DayPlannerTab>('catalog');
  const [plannerRange, setPlannerRange] = React.useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('week'),
    dayjs().startOf('week').add(6, 'day')
  ]);
  const [plannerData, setPlannerData] = React.useState<PlannerMap>({});
  const [weeklyMenuId, setWeeklyMenuId] = React.useState<number | null>(null);
  const [plannerLoading, setPlannerLoading] = React.useState(false);
  const [plannerSaving, setPlannerSaving] = React.useState(false);
  const [plannerDeleteLoading, setPlannerDeleteLoading] = React.useState(false);
  const [plannerModalVisible, setPlannerModalVisible] = React.useState(false);
  const [plannerModalDate, setPlannerModalDate] = React.useState<string | null>(null);
  const [plannerSelectedItems, setPlannerSelectedItems] = React.useState<number[]>([]);
  const [supplierOrderDeadline, setSupplierOrderDeadline] = React.useState<string>('09:00');

  // Combos (supplier)
  const [comboDate, setComboDate] = React.useState<Dayjs>(dayjs());
  const [comboLoading, setComboLoading] = React.useState(false);
  const [combosOnly, setCombosOnly] = React.useState(false);
  const [combosForDay, setCombosForDay] = React.useState<any[]>([]);
  const [comboModalOpen, setComboModalOpen] = React.useState(false);
  const [comboEditing, setComboEditing] = React.useState<ComboDraft | null>(null);
  const [comboCopySource, setComboCopySource] = React.useState<Dayjs>(dayjs());
  const [comboCopyRange, setComboCopyRange] = React.useState<[Dayjs | null, Dayjs | null]>([
    dayjs().startOf('week'),
    dayjs().startOf('week').add(6, 'day')
  ]);
  const [comboCopyMode, setComboCopyMode] = React.useState<'replace' | 'add'>('replace');
  const [comboCopyCombosOnly, setComboCopyCombosOnly] = React.useState(true);

  const plannerDates = React.useMemo(() => {
    const [start, end] = plannerRange;
    if (!start || !end) return [];

    // Не показываем прошедшие даты. "Сегодня" показываем только до дедлайна приёма заказов.
    const now = dayjs();
    const [hStr, mStr] = String(supplierOrderDeadline || '09:00')
      .trim()
      .split(':');
    const h = Number(hStr);
    const m = Number(mStr);
    const deadlineMoment =
      Number.isFinite(h) && Number.isFinite(m)
        ? now.startOf('day').hour(h).minute(m).second(0).millisecond(0)
        : now.startOf('day').hour(9).minute(0).second(0).millisecond(0);
    const minVisibleDate = now.isBefore(deadlineMoment)
      ? now.startOf('day')
      : now.add(1, 'day').startOf('day');

    const dates: Dayjs[] = [];
    let cursor = start.startOf('day');
    const last = end.startOf('day');
    while (cursor.isSame(last, 'day') || cursor.isBefore(last)) {
      if (!cursor.isBefore(minVisibleDate, 'day')) {
        dates.push(cursor);
      }
      cursor = cursor.add(1, 'day');
    }
    return dates;
  }, [plannerRange, supplierOrderDeadline]);

  const buildEmptyPlannerMap = React.useCallback(() => {
    const map: PlannerMap = {};
    plannerDates.forEach((date) => {
      map[date.format('YYYY-MM-DD')] = [];
    });
    return map;
  }, [plannerDates]);

  const hasAnyPlannedItems = React.useMemo(
    () => Object.values(plannerData).some((entries) => entries.length > 0),
    [plannerData]
  );

  const categoryOptions = React.useMemo(() => {
    const set = new Set<string>();
    for (const it of items) {
      if (it?.category) set.add(String(it.category));
    }
    return Array.from(set)
      .sort((a, b) => {
        const orderA = categoryOrder[a] ?? Number.MAX_SAFE_INTEGER;
        const orderB = categoryOrder[b] ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB || a.localeCompare(b, 'ru');
      })
      .map((c) => ({
        value: c,
        label: categoryStyles[c]?.label ?? c
      }));
  }, [items]);

  const filteredCatalogItems = React.useMemo(() => {
    let out = items.slice();

    if (catalogFilterCategories.length > 0) {
      const set = new Set(catalogFilterCategories);
      out = out.filter((it) => it?.category && set.has(String(it.category)));
    }

    if (catalogFilterAvailable !== 'all') {
      const want = catalogFilterAvailable === 'available';
      out = out.filter((it) => Boolean(it?.isAvailable) === want);
    }

    if (catalogFilterFlags.length > 0) {
      out = out.filter((it) => catalogFilterFlags.every((k) => Boolean(it?.[k])));
    }

    if (catalogPriceMin != null && Number.isFinite(catalogPriceMin)) {
      out = out.filter((it) => typeof it?.price === 'number' && it.price >= catalogPriceMin);
    }
    if (catalogPriceMax != null && Number.isFinite(catalogPriceMax)) {
      out = out.filter((it) => typeof it?.price === 'number' && it.price <= catalogPriceMax);
    }

    return out;
  }, [
    items,
    catalogFilterCategories,
    catalogFilterAvailable,
    catalogFilterFlags,
    catalogPriceMin,
    catalogPriceMax
  ]);

  const resolveImageUrl = React.useCallback((path?: string | null) => {
    if (!path) return undefined;
    return path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  }, []);

  const loadItems = React.useCallback(async () => {
    if (!user || user.role !== 'supplier' || !user.supplierId) {
      if (user && user.role !== 'supplier') {
        message.error('Доступ к управлению меню есть только у поставщиков');
      } else if (user) {
        message.error('Не удалось определить профиль поставщика');
      }
      setItems([]);
      return;
    }

    setLoading(true);
    try {
      const data = await menuAPI.getItems({ supplierId: user.supplierId });
      setItems(data);
    } catch (error: any) {
      console.error('Error loading menu:', error);
      message.error(error?.response?.data?.error || 'Не удалось загрузить меню');
    } finally {
      setLoading(false);
    }
  }, [user]);

  const loadPlannerData = React.useCallback(async () => {
    if (!user?.supplierId) return;
    const [start, end] = plannerRange;
    if (!start || !end) {
      setPlannerData({});
      setWeeklyMenuId(null);
      return;
    }

    setPlannerLoading(true);
    try {
      const startStr = start.format('YYYY-MM-DD');
      const endStr = end.format('YYYY-MM-DD');
      const data = await menuAPI.getWeeklyMenus({
        supplierId: user.supplierId,
        startDate: startStr,
        endDate: endStr
      });

      if (!Array.isArray(data) || data.length === 0) {
        setPlannerData(buildEmptyPlannerMap());
        setWeeklyMenuId(null);
        return;
      }

      const menu = data.find((entry: any) => dayjs(entry.weekStartDate).isSame(start, 'day')) || data[0];

      const map = buildEmptyPlannerMap();
      (menu.menuItems || []).forEach((entry: any) => {
        const dateKey = entry.specificDate
          ? dayjs(entry.specificDate).format('YYYY-MM-DD')
          : entry.dayOfWeek
          ? plannerDates.find((date) => date.format('dddd').toLowerCase() === entry.dayOfWeek)?.format('YYYY-MM-DD')
          : undefined;

        if (!dateKey || !map[dateKey]) return;

        map[dateKey].push({
          id: entry.id,
          menuItemId: entry.menuItemId,
          menuItem: entry.menuItem,
          specificDate: entry.specificDate ? dayjs(entry.specificDate).format('YYYY-MM-DD') : null,
          dayOfWeek: entry.dayOfWeek,
          availableQuantity: entry.availableQuantity,
          isAvailable: entry.isAvailable
        });
      });

      setPlannerData(map);
      setWeeklyMenuId(menu.id);
    } catch (error) {
      console.error('Load weekly plan error:', error);
      message.error('Не удалось загрузить план меню');
    } finally {
      setPlannerLoading(false);
    }
  }, [buildEmptyPlannerMap, plannerDates, plannerRange, user]);

  React.useEffect(() => {
    if (user?.role === 'supplier' && user.supplierId) {
      loadItems();
    }
  }, [user?.role, user?.supplierId, loadItems]);

  React.useEffect(() => {
    const loadRules = async () => {
      if (user?.role !== 'supplier') return;
      try {
        const rules = await suppliersAPI.getMyOrderRules();
        if (rules?.orderDeadline && /^\d{1,2}:\d{2}$/.test(rules.orderDeadline)) {
          setSupplierOrderDeadline(rules.orderDeadline);
        }
      } catch (e) {
        // ignore: fallback stays default
      }
    };
    loadRules();
  }, [user?.role]);

  React.useEffect(() => {
    if (!imageModalVisible || !imageItem) return;
    const fresh = items.find((item) => item.id === imageItem.id);
    if (fresh) setImageItem(fresh);
  }, [imageModalVisible, imageItem, items]);

  React.useEffect(() => {
    if (activeTab === 'planner') {
      loadPlannerData();
    }
  }, [activeTab, loadPlannerData]);

  React.useEffect(() => {
    if (activeTab !== 'planner') return;
    setPlannerData((prev) => {
      const base = buildEmptyPlannerMap();
      Object.entries(prev).forEach(([dateKey, entries]) => {
        if (base[dateKey] !== undefined) {
          base[dateKey] = entries;
        }
      });
      return base;
    });
  }, [activeTab, buildEmptyPlannerMap]);

  const loadCombosForDate = React.useCallback(
    async (d: Dayjs) => {
      if (!user?.supplierId) return;
      setComboLoading(true);
      try {
        const dateStr = d.format('YYYY-MM-DD');
        const data = await combosAPI.getDay(dateStr, { supplierId: user.supplierId });
        setCombosOnly(!!data?.combosOnly);
        setCombosForDay(Array.isArray(data?.combos) ? data.combos : []);
      } catch (e: any) {
        console.error(e);
        message.error(e?.response?.data?.error || 'Не удалось загрузить комбо на дату');
      } finally {
        setComboLoading(false);
      }
    },
    [user?.supplierId]
  );

  React.useEffect(() => {
    if (activeTab === 'combos') {
      loadCombosForDate(comboDate);
    }
  }, [activeTab, comboDate, loadCombosForDate]);

  const openCreateCombo = React.useCallback(() => {
    setComboEditing({
      title: '',
      description: null,
      allowCustomization: true,
      pricingMode: 'fixed',
      fixedPrice: 349,
      isActive: true,
      groups: [
        { title: 'Салат', requiredCount: 1, menuItemIds: [] },
        { title: 'Суп', requiredCount: 1, menuItemIds: [] },
        { title: 'Горячее с гарниром', requiredCount: 1, menuItemIds: [] },
      ]
    });
    setComboModalOpen(true);
  }, []);

  const openEditCombo = React.useCallback((combo: any) => {
    const groups: ComboGroupDraft[] = (combo?.groups || []).map((g: any) => ({
      title: g.title,
      requiredCount: g.requiredCount ?? 1,
      menuItemIds: (g.items || []).map((it: any) => it.menuItemId)
    }));
    setComboEditing({
      id: combo.id,
      title: combo.title ?? '',
      description: combo.description ?? null,
      allowCustomization: !!combo.allowCustomization,
      pricingMode: (combo.pricingMode as ComboPricingMode) ?? 'fixed',
      fixedPrice: combo.fixedPrice ?? null,
      isActive: combo.isActive ?? true,
      groups: groups.length ? groups : [{ title: 'Состав', requiredCount: 1, menuItemIds: [] }]
    });
    setComboModalOpen(true);
  }, []);

  const saveCombo = React.useCallback(async () => {
    if (!user?.supplierId || !comboEditing) return;
    const dateStr = comboDate.format('YYYY-MM-DD');
    const title = comboEditing.title.trim();
    if (!title) {
      message.warning('Укажите название комбо');
      return;
    }
    if (!comboEditing.groups.length) {
      message.warning('Добавьте хотя бы одну группу');
      return;
    }
    for (const g of comboEditing.groups) {
      if (!g.title.trim()) {
        message.warning('У каждой группы должно быть название');
        return;
      }
      if (!g.menuItemIds.length) {
        message.warning(`В группе «${g.title}» нет блюд`);
        return;
      }
      if (!g.requiredCount || g.requiredCount < 1) {
        message.warning(`В группе «${g.title}» requiredCount должен быть ≥ 1`);
        return;
      }
      if (g.requiredCount > g.menuItemIds.length) {
        message.warning(`В группе «${g.title}» requiredCount больше, чем количество блюд`);
        return;
      }
    }
    if (comboEditing.pricingMode === 'fixed' && !(typeof comboEditing.fixedPrice === 'number')) {
      message.warning('Для фиксированной цены укажите fixedPrice');
      return;
    }

    const body = {
      title,
      description: comboEditing.description ?? null,
      allowCustomization: !!comboEditing.allowCustomization,
      pricingMode: comboEditing.pricingMode,
      fixedPrice: comboEditing.pricingMode === 'fixed' ? (comboEditing.fixedPrice ?? null) : null,
      isActive: comboEditing.isActive,
      groups: comboEditing.groups.map((g, idx) => ({
        title: g.title.trim(),
        requiredCount: g.requiredCount,
        sortOrder: idx,
        items: g.menuItemIds.map((menuItemId, j) => ({ menuItemId, sortOrder: j }))
      }))
    };

    setComboLoading(true);
    try {
      if (comboEditing.id) {
        await combosAPI.updateCombo(comboEditing.id, { ...body, supplierId: user.supplierId });
        message.success('Комбо обновлено');
      } else {
        await combosAPI.createCombo(dateStr, { ...body, supplierId: user.supplierId });
        message.success('Комбо создано');
      }
      setComboModalOpen(false);
      setComboEditing(null);
      await loadCombosForDate(comboDate);
    } catch (e: any) {
      console.error(e);
      message.error(e?.response?.data?.error || 'Не удалось сохранить комбо');
    } finally {
      setComboLoading(false);
    }
  }, [comboDate, comboEditing, loadCombosForDate, user?.supplierId]);

  const deleteCombo = React.useCallback(
    async (id: number) => {
      if (!user?.supplierId) return;
      setComboLoading(true);
      try {
        await combosAPI.deleteCombo(id);
        message.success('Комбо удалено');
        await loadCombosForDate(comboDate);
      } catch (e: any) {
        console.error(e);
        message.error(e?.response?.data?.error || 'Не удалось удалить комбо');
      } finally {
        setComboLoading(false);
      }
    },
    [comboDate, loadCombosForDate, user?.supplierId]
  );

  const toggleCombosOnly = React.useCallback(
    async (value: boolean) => {
      if (!user?.supplierId) return;
      const dateStr = comboDate.format('YYYY-MM-DD');
      setCombosOnly(value);
      try {
        await combosAPI.setDay(dateStr, { combosOnly: value, supplierId: user.supplierId });
      } catch (e: any) {
        console.error(e);
        message.error(e?.response?.data?.error || 'Не удалось обновить настройку');
        setCombosOnly((prev) => !prev);
      }
    },
    [comboDate, user?.supplierId]
  );

  const copyCombos = React.useCallback(async () => {
    if (!user?.supplierId) return;
    const [start, end] = comboCopyRange;
    if (!start || !end) {
      message.warning('Выберите диапазон дат');
      return;
    }
    setComboLoading(true);
    try {
      await combosAPI.copy({
        supplierId: user.supplierId,
        sourceDate: comboCopySource.format('YYYY-MM-DD'),
        targetStart: start.format('YYYY-MM-DD'),
        targetEnd: end.format('YYYY-MM-DD'),
        mode: comboCopyMode,
        copyCombosOnly: comboCopyCombosOnly
      });
      message.success('Комбо скопированы');
      await loadCombosForDate(comboDate);
    } catch (e: any) {
      console.error(e);
      message.error(e?.response?.data?.error || 'Не удалось скопировать комбо');
    } finally {
      setComboLoading(false);
    }
  }, [comboCopyCombosOnly, comboCopyMode, comboCopyRange, comboCopySource, comboDate, loadCombosForDate, user?.supplierId]);

  const createItem = async () => {
    try {
      const values = await form.validateFields();
      await menuAPI.createItem(values);
      message.success('Блюдо создано');
      setCreateVisible(false);
      form.resetFields();
      loadItems();
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка создания');
    }
  };

  const editItem = async () => {
    if (!current) return;
    try {
      const values = await form.validateFields();
      await menuAPI.updateItem(current.id, values);
      message.success('Блюдо обновлено');
      setEditVisible(false);
      form.resetFields();
      loadItems();
    } catch (error: any) {
      message.error(error?.response?.data?.error || 'Ошибка обновления');
    }
  };

  const deleteItem = async (id: number) => {
    Modal.confirm({
      title: 'Удалить блюдо?',
      content: 'Если блюдо есть в активных заказах — оно будет переведено в архив (скрыто из меню). Уже размещённые заказы не пострадают.',
      async onOk() {
        try {
          const res = await menuAPI.deleteItem(id);
          if (res?.archived) {
            message.warning('Блюдо переведено в архив: оно участвует в активных заказах. Из меню скрыто.');
          } else {
            message.success('Блюдо удалено');
          }
          loadItems();
        } catch (error: any) {
          message.error(error?.response?.data?.error || 'Ошибка удаления');
        }
      }
    });
  };

  const bulkDeleteSelected = async () => {
    if (!selectedRowKeys.length) return;
    if (bulkDeleteConfirmText.trim().toUpperCase() !== 'УДАЛИТЬ') {
      message.warning('Введите слово УДАЛИТЬ для подтверждения');
      return;
    }
    setLoading(true);
    try {
      const ids = selectedRowKeys.map((k) => Number(k)).filter((x) => Number.isFinite(x) && x > 0);
      const res = await menuAPI.bulkDeleteItems(ids);
      if (res?.message) {
        message.warning(res.message);
      } else {
        message.success(`Удалено блюд: ${res.deletedCount}`);
      }
      setBulkDeleteOpen(false);
      setBulkDeleteConfirmText('');
      setSelectedRowKeys([]);
      await loadItems();
    } catch (error: any) {
      console.error('Bulk delete error:', error);
      message.error(error?.response?.data?.error || 'Не удалось удалить блюда');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (record: any) => {
    setCurrent(record);
    form.setFieldsValue(record);
    setEditVisible(true);
  };

  const openImageModal = (record: any) => {
    setImageItem(record);
    setImageModalVisible(true);
  };

  type CustomRequestOptions = Parameters<NonNullable<UploadProps['customRequest']>>[0];

  const handleImageUpload: UploadProps['customRequest'] = async (options) => {
    if (!imageItem) return;
    const { file, onError, onSuccess } = options as CustomRequestOptions;

    setUploadingImage(true);
    try {
      await menuAPI.uploadImage(imageItem.id, file as File);
      message.success('Фотография успешно загружена');
      onSuccess?.({});
      setImageModalVisible(false);
      setImageItem(null);
      loadItems();
    } catch (error: any) {
      console.error('Upload image error:', error);
      message.error(error?.response?.data?.error || 'Не удалось загрузить фото');
      onError?.(error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    if (!imageItem) return;
    Modal.confirm({
      title: 'Удалить фотографию?',
      content: 'Фотография будет удалена и сотрудники больше не увидят её в меню.',
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      async onOk() {
        try {
          setUploadingImage(true);
          await menuAPI.deleteImage(imageItem.id);
          message.success('Фотография удалена');
          setImageModalVisible(false);
          setImageItem(null);
          loadItems();
        } catch (error: any) {
          console.error('Delete image error:', error);
          message.error(error?.response?.data?.error || 'Не удалось удалить фото');
        } finally {
          setUploadingImage(false);
        }
      }
    });
  };

  type ExcelCustomRequestOptions = Parameters<NonNullable<UploadProps['customRequest']>>[0];

  const handleExcelImport: UploadProps['customRequest'] = async (options) => {
    const { file, onError, onSuccess } = options as ExcelCustomRequestOptions;
    setUploadingExcel(true);
    try {
      const result = await menuAPI.importItemsExcel(file as File, { mode: 'upsert' });
      message.success(`Импорт завершён: создано ${result.created}, обновлено ${result.updated}`);
      onSuccess?.(result as any);
      loadItems();
    } catch (error: any) {
      console.error('Excel import error:', error);
      message.error(error?.response?.data?.error || 'Не удалось импортировать Excel');
      onError?.(error);
    } finally {
      setUploadingExcel(false);
    }
  };

  const checkDuplicates = async () => {
    setDuplicatesLoading(true);
    setDuplicatesOpen(true);
    try {
      const report = await menuAPI.findDuplicateItems();
      setDuplicateReport(report);
    } catch (error: any) {
      console.error('Find duplicates error:', error);
      message.error(error?.response?.data?.error || 'Не удалось проверить дубли');
      setDuplicateReport(null);
    } finally {
      setDuplicatesLoading(false);
    }
  };

  const removeDuplicates = () => {
    if (!duplicateReport?.duplicateCount) return;
    Modal.confirm({
      title: 'Удалить дубли?',
      content: (
        <div>
          <p>
            Будет удалено <b>{duplicateReport.duplicateCount}</b> блюд из{' '}
            <b>{duplicateReport.groupCount}</b> групп.
          </p>
          <p>В каждой группе останется самая ранняя запись (минимальный ID). Ссылки в плане меню и черновых заказах будут перенесены на неё.</p>
          <p>Блюда в активных заказах пропускаются.</p>
        </div>
      ),
      okText: 'Удалить дубли',
      okType: 'danger',
      cancelText: 'Отмена',
      async onOk() {
        setDuplicatesRemoving(true);
        try {
          const result = await menuAPI.removeDuplicateItems();
          const skipped = result.skipped?.length ?? 0;
          message.success(
            `Удалено дублей: ${result.removedCount ?? 0}` +
              (skipped > 0 ? `, пропущено: ${skipped}` : '')
          );
          if (skipped > 0) {
            Modal.info({
              title: 'Часть дублей не удалена',
              content: (
                <ul style={{ paddingLeft: 18, marginBottom: 0 }}>
                  {result.skipped!.map((s) => (
                    <li key={s.id}>
                      #{s.id} {s.name} — {s.reason}
                    </li>
                  ))}
                </ul>
              )
            });
          }
          await loadItems();
          await checkDuplicates();
        } catch (error: any) {
          console.error('Remove duplicates error:', error);
          message.error(error?.response?.data?.error || 'Не удалось удалить дубли');
        } finally {
          setDuplicatesRemoving(false);
        }
      }
    });
  };

  type PlannerExcelCustomRequestOptions = Parameters<NonNullable<UploadProps['customRequest']>>[0];

  const handlePlannerExcelImport: UploadProps['customRequest'] = async (options) => {
    const { file, onError, onSuccess } = options as PlannerExcelCustomRequestOptions;
    setUploadingPlannerExcel(true);
    try {
      const result = await menuAPI.importWeeklyMenuExcel(file as File, { mode: 'replace_days' });
      message.success(
        `Импорт меню завершён: строк ${result.importedRows ?? result.validRows ?? 0}, недель затронуто ${result.affectedWeeks ?? 0}`
      );
      onSuccess?.(result as any);
      if (activeTab === 'planner') {
        await loadPlannerData();
      }
    } catch (error: any) {
      console.error('Planner Excel import error:', error);
      message.error(error?.response?.data?.error || 'Не удалось импортировать меню из Excel');
      onError?.(error);
    } finally {
      setUploadingPlannerExcel(false);
    }
  };

  const downloadExcelTemplate = async () => {
    setUploadingExcel(true);
    try {
      const blob = await menuAPI.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'menu_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Template download error:', error);
      message.error(error?.response?.data?.error || 'Не удалось скачать шаблон');
    } finally {
      setUploadingExcel(false);
    }
  };

  const downloadPlannerExcelTemplate = async () => {
    setUploadingPlannerExcel(true);
    try {
      const blob = await menuAPI.downloadWeeklyMenuImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'weekly_menu_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Weekly menu template download error:', error);
      message.error(error?.response?.data?.error || 'Не удалось скачать шаблон меню');
    } finally {
      setUploadingPlannerExcel(false);
    }
  };

  const handlePlannerRangeChange = (values: null | (Dayjs | null)[]) => {
    if (!values || values.length !== 2 || !values[0] || !values[1]) {
      setPlannerRange([null, null]);
      setPlannerData({});
      setWeeklyMenuId(null);
      return;
    }
    setPlannerRange([values[0].startOf('day'), values[1].startOf('day')]);
  };

  const openPlannerModal = (dateKey: string) => {
    const existing = plannerData[dateKey] || [];
    setPlannerSelectedItems(existing.map((entry) => entry.menuItemId));
    setPlannerModalDate(dateKey);
    setPlannerModalVisible(true);
  };

  const handlePlannerModalCancel = () => {
    setPlannerModalVisible(false);
    setPlannerModalDate(null);
    setPlannerSelectedItems([]);
  };

  const handlePlannerModalOk = () => {
    if (!plannerModalDate) return;

    const existing = plannerData[plannerModalDate] || [];
    const nextEntries: PlannedMenuItem[] = plannerSelectedItems.map((id) => {
      const found = existing.find((entry) => entry.menuItemId === id);
      if (found) return found;
      const menuItem = items.find((item) => item.id === id);
      return {
        menuItemId: id,
        menuItem,
        specificDate: plannerModalDate
      };
    });

    setPlannerData((prev) => ({
      ...prev,
      [plannerModalDate]: nextEntries
    }));
    handlePlannerModalCancel();
  };

  const handleRemovePlannerItem = (dateKey: string, menuItemId: number) => {
    setPlannerData((prev) => ({
      ...prev,
      [dateKey]: (prev[dateKey] || []).filter((entry) => entry.menuItemId !== menuItemId)
    }));
  };

  const handleClearPlanner = () => {
    setPlannerData(buildEmptyPlannerMap());
  };

  const handleSavePlanner = async () => {
    if (!user?.supplierId) {
      message.error('Не удалось определить поставщика');
      return;
    }

    const [start, end] = plannerRange;
    if (!start || !end) {
      message.warning('Укажите диапазон дат');
      return;
    }

    setPlannerSaving(true);
    try {
      const payload = {
        id: weeklyMenuId ?? undefined,
        weekStartDate: start.format('YYYY-MM-DD'),
        weekEndDate: end.format('YYYY-MM-DD'),
        items: Object.entries(plannerData).flatMap(([dateKey, entries]) =>
          entries.map((entry) => ({
            menuItemId: entry.menuItemId,
            dayOfWeek: entry.dayOfWeek ?? null,
            specificDate: dateKey,
            availableQuantity: entry.availableQuantity ?? 100,
            isAvailable: entry.isAvailable ?? true
          }))
        )
      };

      const response = await menuAPI.saveWeeklyMenu(payload);
      message.success('План меню сохранён');

      if (response) {
        const responseStart = dayjs(response.weekStartDate).startOf('day');
        const responseEnd = dayjs(response.weekEndDate).startOf('day');
        setPlannerRange([responseStart, responseEnd]);
        setWeeklyMenuId(response.id);

        const dates: Dayjs[] = [];
        let cursor = responseStart.clone();
        while (cursor.isSame(responseEnd, 'day') || cursor.isBefore(responseEnd)) {
          dates.push(cursor);
          cursor = cursor.add(1, 'day');
        }

        const responseMap: PlannerMap = {};
        dates.forEach((date) => {
          responseMap[date.format('YYYY-MM-DD')] = [];
        });

        (response.menuItems || []).forEach((entry: any) => {
          const dateKey = entry.specificDate
            ? dayjs(entry.specificDate).format('YYYY-MM-DD')
            : entry.dayOfWeek
            ? dates.find((date) => date.format('dddd').toLowerCase() === entry.dayOfWeek)?.format('YYYY-MM-DD')
            : undefined;

          if (!dateKey) return;
          if (!responseMap[dateKey]) responseMap[dateKey] = [];

          responseMap[dateKey].push({
            id: entry.id,
            menuItemId: entry.menuItemId,
            menuItem: entry.menuItem,
            specificDate: entry.specificDate ? dayjs(entry.specificDate).format('YYYY-MM-DD') : null,
            dayOfWeek: entry.dayOfWeek,
            availableQuantity: entry.availableQuantity,
            isAvailable: entry.isAvailable
          });
        });

        setPlannerData(responseMap);
      }
    } catch (error: any) {
      console.error('Save weekly plan error:', error);
      message.error(error?.response?.data?.error || 'Не удалось сохранить план');
    } finally {
      setPlannerSaving(false);
    }
  };

  const handleDeletePlanner = async () => {
    if (!weeklyMenuId) {
      message.info('Нет сохранённого плана для удаления');
      return;
    }

    setPlannerDeleteLoading(true);
    try {
      await menuAPI.deleteWeeklyMenu(weeklyMenuId);
      message.success('План меню удалён');
      setWeeklyMenuId(null);
      setPlannerData(buildEmptyPlannerMap());
    } catch (error: any) {
      console.error('Delete weekly plan error:', error);
      message.error(error?.response?.data?.error || 'Не удалось удалить план');
    } finally {
      setPlannerDeleteLoading(false);
    }
  };

  const columns = [
    {
      title: 'Фото',
      dataIndex: 'thumbnailUrl',
      width: 150,
      sorter: (a: any, b: any) =>
        Number(Boolean(a.thumbnailUrl || a.imageUrl)) - Number(Boolean(b.thumbnailUrl || b.imageUrl)),
      render: (_: string, record: any) => {
        const previewSrc = resolveImageUrl(record.thumbnailUrl || record.imageUrl);
        const fullSrc = resolveImageUrl(record.imageUrl);
        return previewSrc ? (
          <Image
            src={previewSrc}
            alt={record.name}
            width={110}
            height={80}
            style={{ objectFit: 'cover', borderRadius: 8, border: '1px solid #f0f0f0' }}
            preview={fullSrc ? { src: fullSrc } : false}
            placeholder={<div style={{ width: 110, height: 80, background: '#fafafa' }} />}
          />
        ) : (
          <div
            style={{
              width: 110,
              height: 80,
              border: '1px dashed #d9d9d9',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#bfbfbf',
              fontSize: 12
            }}
          >
            Нет фото
          </div>
        );
      }
    },
    {
      title: 'Блюдо',
      dataIndex: 'name',
      ellipsis: true,
      width: 260,
      sorter: (a: any, b: any) => a.name.localeCompare(b.name, 'ru'),
      render: (_: string, record: any) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Text strong style={{ fontSize: 15 }}>
            {record.name}
          </Text>
          <Space size={6} wrap>
            <Tag color="purple" bordered={false} style={{ marginInlineEnd: 0 }}>
              #{record.id}
            </Tag>
            {record.description && (
              <Tooltip title={record.description} placement="topLeft">
                <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
              </Tooltip>
            )}
          </Space>
        </div>
      )
    },
    {
      title: 'Категория',
      dataIndex: 'category',
      width: 170,
      sorter: (a: any, b: any) => {
        const orderA = categoryOrder[a.category] ?? Number.MAX_SAFE_INTEGER;
        const orderB = categoryOrder[b.category] ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB || a.category.localeCompare(b.category, 'ru');
      },
      render: (category: string) => {
        const config = categoryStyles[category] || { label: category, color: '#d9d9d9' };
        return (
          <Tag color={config.color} style={{ fontWeight: 500 }}>
            {config.label}
          </Tag>
        );
      }
    },
    {
      title: 'Пищевая ценность',
      key: 'nutrition',
      width: 200,
      sorter: (a: any, b: any) => (a.weight ?? 0) - (b.weight ?? 0) || (a.calories ?? 0) - (b.calories ?? 0),
      render: (record: any) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.weightText || formatGrams(record.weight)}</Text>
          <Text type="secondary">{formatCalories(record.calories)}</Text>
        </Space>
      )
    },
    {
      title: 'Цена',
      dataIndex: 'price',
      width: 140,
      sorter: (a: any, b: any) => (a.price ?? 0) - (b.price ?? 0),
      render: (price: number) => <Text strong style={{ fontSize: 16 }}>{formatCurrency(price)}</Text>
    },
    {
      title: 'Признаки',
      key: 'attributes',
      width: 220,
      sorter: (a: any, b: any) => {
        const aCount = attributeChips.filter((chip) => a?.[chip.key]).length;
        const bCount = attributeChips.filter((chip) => b?.[chip.key]).length;
        return aCount - bCount;
      },
      render: (record: any) => {
        const chips = attributeChips.filter((chip) => record?.[chip.key]);
        if (!chips.length) {
          return <Tag color="default">Обычное</Tag>;
        }
        return (
          <Space wrap size={4}>
            {chips.map((chip) => (
              <Tag key={chip.key} color={chip.color}>
                {chip.label}
              </Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'Статус',
      dataIndex: 'isAvailable',
      width: 140,
      sorter: (a: any, b: any) => Number(b.isAvailable) - Number(a.isAvailable),
      render: (available: boolean) => {
        const config = availabilityConfig[String(available) as keyof typeof availabilityConfig];
        return <Badge color={config.color} text={config.text} />;
      }
    },
    {
      title: 'Действия',
      key: 'actions',
      fixed: 'right' as const,
      width: 150,
      render: (_: any, record: any) => (
        <Space.Compact size="small">
          <Tooltip title={record.imageUrl ? 'Обновить фото' : 'Добавить фото'} placement="top">
            <Button icon={<PictureOutlined />} onClick={() => openImageModal(record)} />
          </Tooltip>
          <Tooltip title="Редактировать блюдо" placement="top">
            <Button icon={<EditOutlined />} onClick={() => openEdit(record)} />
          </Tooltip>
          <Tooltip title="Удалить блюдо" placement="top">
            <Button icon={<DeleteOutlined />} danger onClick={() => deleteItem(record.id)} />
          </Tooltip>
        </Space.Compact>
      )
    }
  ];

  const renderCatalog = () => (
    <Card
      title="Меню поставщика"
      loading={loading}
      extra={
        <Space size={12}>
          <Button
            danger
            disabled={selectedRowKeys.length === 0}
            onClick={() => {
              setBulkDeleteConfirmText('');
              setBulkDeleteOpen(true);
            }}
          >
            Удалить выбранные ({selectedRowKeys.length})
          </Button>
          <Button icon={<SearchOutlined />} onClick={checkDuplicates} loading={duplicatesLoading}>
            Проверить на дубли
          </Button>
          <Button onClick={downloadExcelTemplate} disabled={uploadingExcel}>
            Скачать шаблон
          </Button>
          <Upload
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            multiple={false}
            maxCount={1}
            showUploadList={false}
            customRequest={handleExcelImport}
            disabled={uploadingExcel}
          >
            <Button icon={<UploadOutlined />} loading={uploadingExcel}>
              Импорт из Excel
            </Button>
          </Upload>
          <Tooltip title="Создать новое блюдо">
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                form.resetFields();
                setCreateVisible(true);
              }}
            >
              Добавить блюдо
            </Button>
          </Tooltip>
        </Space>
      }
      styles={{
        header: {
          padding: '20px 24px 12px',
          borderBottom: '1px solid #f0f0f0'
        },
        body: {
          padding: 0
        }
      }}
    >
      <div style={{ padding: 24 }}>
        <Space size="large" wrap style={{ marginBottom: 16 }}>
          <StatisticCard title="Всего блюд" value={items.length} />
          <StatisticCard title="Показано" value={filteredCatalogItems.length} />
          <StatisticCard title="В наличии" value={items.filter((item) => item.isAvailable).length} />
          <StatisticCard title="С фото" value={items.filter((item) => item.thumbnailUrl || item.imageUrl).length} />
          <StatisticCard
            title="Без ограничений"
            value={items.filter((item) => !item.isVegan && !item.isHalal && !item.isLenten).length}
          />
        </Space>

        <Card size="small" style={{ marginBottom: 16 }} bodyStyle={{ padding: 12 }}>
          <Space size={12} wrap style={{ width: '100%' }}>
            <div style={{ minWidth: 260 }}>
              <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                Категории
              </Text>
              <Select
                mode="multiple"
                allowClear
                placeholder="Выберите категории"
                value={catalogFilterCategories}
                onChange={(v) => setCatalogFilterCategories(v)}
                options={categoryOptions}
                style={{ width: '100%' }}
                maxTagCount="responsive"
              />
            </div>

            <div style={{ minWidth: 180 }}>
              <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                Статус
              </Text>
              <Select
                value={catalogFilterAvailable}
                onChange={(v) => setCatalogFilterAvailable(v)}
                style={{ width: '100%' }}
                options={[
                  { value: 'all', label: 'Все' },
                  { value: 'available', label: 'В наличии' },
                  { value: 'unavailable', label: 'Нет в наличии' }
                ]}
              />
            </div>

            <div style={{ minWidth: 260 }}>
              <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                Признаки (можно совместно)
              </Text>
              <Checkbox.Group
                value={catalogFilterFlags}
                onChange={(v) => setCatalogFilterFlags(v as any)}
                options={[
                  { label: 'Веган', value: 'isVegan' },
                  { label: 'Постное', value: 'isLenten' },
                  { label: 'Халяль', value: 'isHalal' }
                ]}
              />
            </div>

            <div style={{ minWidth: 220 }}>
              <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>
                Цена
              </Text>
              <Space size={8}>
                <InputNumber
                  min={0}
                  placeholder="от"
                  value={catalogPriceMin}
                  onChange={(v) => setCatalogPriceMin(typeof v === 'number' ? v : null)}
                />
                <InputNumber
                  min={0}
                  placeholder="до"
                  value={catalogPriceMax}
                  onChange={(v) => setCatalogPriceMax(typeof v === 'number' ? v : null)}
                />
              </Space>
            </div>

            <Button
              onClick={() => {
                setCatalogFilterCategories([]);
                setCatalogFilterAvailable('all');
                setCatalogFilterFlags([]);
                setCatalogPriceMin(null);
                setCatalogPriceMax(null);
              }}
            >
              Сбросить фильтры
            </Button>
          </Space>
        </Card>

        <Table
          rowKey="id"
          columns={columns as any}
          dataSource={filteredCatalogItems}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys)
          }}
          pagination={false}
          locale={{ emptyText: 'Меню пока пустое' }}
          scroll={{ x: isMobile ? 'max-content' : 1100 }}
          size={isMobile ? 'small' : 'middle'}
          tableLayout="fixed"
          bordered
        />
      </div>

      <Modal
        open={bulkDeleteOpen}
        title="Массовое удаление блюд"
        okText="Удалить"
        cancelText="Отмена"
        okButtonProps={{ danger: true, disabled: selectedRowKeys.length === 0 }}
        onCancel={() => setBulkDeleteOpen(false)}
        onOk={bulkDeleteSelected}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            message={`Будет удалено блюд: ${selectedRowKeys.length}`}
            description="Операция необратима. Чтобы подтвердить, введите слово УДАЛИТЬ."
          />
          <AntInput
            placeholder="Введите УДАЛИТЬ"
            value={bulkDeleteConfirmText}
            onChange={(e) => setBulkDeleteConfirmText(e.target.value)}
          />
        </Space>
      </Modal>

      <Modal
        open={duplicatesOpen}
        title="Дубли в каталоге"
        width={isMobile ? '100%' : 900}
        onCancel={() => setDuplicatesOpen(false)}
        footer={[
          <Button key="close" onClick={() => setDuplicatesOpen(false)}>
            Закрыть
          </Button>,
          <Button
            key="remove"
            type="primary"
            danger
            disabled={!duplicateReport?.duplicateCount}
            loading={duplicatesRemoving}
            onClick={removeDuplicates}
          >
            Удалить дубли ({duplicateReport?.duplicateCount ?? 0})
          </Button>
        ]}
        destroyOnClose
      >
        {duplicatesLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : !duplicateReport?.groupCount ? (
          <Empty description="Дублей по названию не найдено" />
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Alert
              type="info"
              showIcon
              message={`Найдено ${duplicateReport.groupCount} групп, ${duplicateReport.duplicateCount} лишних записей`}
              description="Сравнение по названию без учёта регистра и лишних пробелов. В каждой группе сохраняется запись с наименьшим ID."
            />
            {duplicateReport.groups.map((group) => (
              <Card
                key={group.key}
                size="small"
                title={
                  <Space wrap>
                    <Text strong>{group.items[0]?.name}</Text>
                    <Tag color="blue">оставить #{group.keepId}</Tag>
                    <Tag color="orange">удалить: {group.duplicateIds.length}</Tag>
                  </Space>
                }
              >
                <Table
                  dataSource={group.items}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  columns={[
                    { title: 'ID', dataIndex: 'id', width: 70 },
                    { title: 'Название', dataIndex: 'name' },
                    {
                      title: 'Категория',
                      dataIndex: 'category',
                      render: (c: string) => categoryStyles[c]?.label || c
                    },
                    {
                      title: 'Цена',
                      dataIndex: 'price',
                      width: 90,
                      render: (p: number) => `${p} ₽`
                    },
                    {
                      title: 'Статус',
                      key: 'action',
                      width: 120,
                      render: (_: unknown, row: { id: number }) =>
                        row.id === group.keepId ? (
                          <Tag color="green">Оставить</Tag>
                        ) : (
                          <Tag color="red">Удалить</Tag>
                        )
                    }
                  ]}
                />
              </Card>
            ))}
          </Space>
        )}
      </Modal>
    </Card>
  );

  const renderPlanner = () => {
    if (!user?.supplierId) {
      return <Alert message="Планирование меню доступно только для поставщиков" type="warning" showIcon />;
    }

    return (
      <Card
        title="Планирование меню"
        extra={
          <Space size="middle" wrap>
            <Button onClick={downloadPlannerExcelTemplate} disabled={uploadingPlannerExcel}>
              Скачать шаблон Excel
            </Button>
            <Upload
              accept=".xlsx"
              showUploadList={false}
              customRequest={handlePlannerExcelImport}
              disabled={uploadingPlannerExcel}
            >
              <Button icon={<UploadOutlined />} loading={uploadingPlannerExcel}>
                Импорт меню из Excel
              </Button>
            </Upload>
            <Button onClick={handleClearPlanner} disabled={!hasAnyPlannedItems && plannerDates.length === 0}>
              Очистить
            </Button>
            <Button danger onClick={handleDeletePlanner} loading={plannerDeleteLoading} disabled={!weeklyMenuId}>
              Удалить план
            </Button>
            <Button
              type="primary"
              onClick={handleSavePlanner}
              loading={plannerSaving}
              disabled={plannerDates.length === 0}
            >
              Сохранить план
            </Button>
          </Space>
        }
        styles={{
          header: {
            padding: '20px 24px 12px',
            borderBottom: '1px solid #f0f0f0'
          },
          body: {
            padding: 24
          }
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message="Запланируйте блюда на неделю или произвольный период"
            description="Выберите даты и добавьте блюда из каталога. Заказчики увидят актуальное меню на выбранные дни."
          />

          <Space size="middle" wrap>
            <RangePicker value={plannerRange} onChange={handlePlannerRangeChange} allowClear={false} />
            <Button onClick={loadPlannerData} loading={plannerLoading}>
              Загрузить план
            </Button>
          </Space>

          {plannerLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
            </div>
          ) : plannerDates.length === 0 ? (
            <Empty
              description="Выберите диапазон дат, чтобы начать планирование"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {plannerDates.map((date) => {
                const dateStr = date.format('YYYY-MM-DD');
                const planItems = plannerData[dateStr] || [];

                return (
                  <Card
                    key={dateStr}
                    size="small"
                    title={
                      <Space size={8}>
                        <Text strong>{date.format('DD.MM.YYYY')}</Text>
                        <Tag color="gold">{date.format('dddd')}</Tag>
                      </Space>
                    }
                    extra={
                      <Button
                        type="dashed"
                        size="small"
                        icon={<PlusCircleOutlined />}
                        onClick={() => openPlannerModal(dateStr)}
                      >
                        Добавить блюда
                      </Button>
                    }
                    bodyStyle={{ padding: 16 }}
                  >
                    {planItems.length > 0 ? (
                      <List
                        dataSource={planItems}
                        rowKey={(item) => `${dateStr}_${item.menuItemId}`}
                        renderItem={(item) => {
                          const menuItem = item.menuItem || items.find((m) => m.id === item.menuItemId);
                          return (
                            <List.Item
                              actions={[
                                <Button
                                  key="remove"
                                  type="link"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => handleRemovePlannerItem(dateStr, item.menuItemId)}
                                >
                                  Удалить
                                </Button>
                              ]}
                            >
                              <List.Item.Meta
                                title={menuItem?.name || `Блюдо #${item.menuItemId}`}
                                description={
                                  menuItem ? (
                                    <Space size="small" wrap>
                                      {menuItem.category && (
                                        <Tag color={categoryStyles[menuItem.category]?.color || 'blue'}>
                                          {categoryStyles[menuItem.category]?.label || menuItem.category}
                                        </Tag>
                                      )}
                                      {(menuItem.weightText || menuItem.weight) && (
                                        <Tag>{menuItem.weightText || formatGrams(menuItem.weight)}</Tag>
                                      )}
                                      {menuItem.calories && <Tag>{formatCalories(menuItem.calories)}</Tag>}
                                      {typeof menuItem.price === 'number' && (
                                        <Tag color="green">{formatCurrency(menuItem.price)}</Tag>
                                      )}
                                    </Space>
                                  ) : (
                                    <Text type="secondary">Блюдо удалено из каталога</Text>
                                  )
                                }
                              />
                            </List.Item>
                          );
                        }}
                      />
                    ) : (
                      <div style={{ padding: '12px 0' }}>
                        <Empty
                          description="Для этой даты пока нет блюд"
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      </div>
                    )}
                  </Card>
                );
              })}
            </Space>
          )}
        </Space>
      </Card>
    );
  };

  const renderCombos = () => {
    const itemOptions = items.map((it) => ({
      label:
        typeof it.price === 'number' && it.price === 0
          ? `${it.name} (вариант комбо, 0 ₽)`
          : `${it.name} (${typeof it.price === 'number' ? `${it.price} ₽` : '—'})`,
      value: it.id
    }));

    return (
      <Card>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Space wrap align="center">
            <Text strong>Дата</Text>
            <DatePicker value={comboDate} onChange={(d) => d && setComboDate(d)} />
            <Space align="center">
              <Text strong>Только комбо</Text>
              <Switch checked={combosOnly} onChange={toggleCombosOnly} />
            </Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateCombo}>
              Создать комбо
            </Button>
          </Space>

          <Alert
            type="info"
            showIcon
            message="Фиксированная цена набора"
            description={
              <>
                Для сета вроде «349 ₽, внутри выбор салат/суп/горячее» используйте режим{' '}
                <Text strong>Фикс</Text> и укажите цену набора. Цены блюд в каталоге при этом{' '}
                <Text strong>не суммируются</Text>. Варианты можно завести с ценой{' '}
                <Text strong>0 ₽</Text> — они не попадут в штучное меню сотрудникам.
              </>
            }
          />

          <Spin spinning={comboLoading}>
            {combosForDay.length === 0 ? (
              <Empty description="На эту дату комбо не настроены" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <List
                dataSource={combosForDay}
                renderItem={(combo: any) => (
                  <List.Item
                    actions={[
                      <Button key="edit" icon={<EditOutlined />} onClick={() => openEditCombo(combo)}>
                        Редактировать
                      </Button>,
                      <Popconfirm
                        key="del"
                        title="Удалить комбо?"
                        okText="Удалить"
                        cancelText="Отмена"
                        onConfirm={() => deleteCombo(combo.id)}
                      >
                        <Button danger icon={<DeleteOutlined />}>
                          Удалить
                        </Button>
                      </Popconfirm>
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Space wrap>
                          <Text strong>{combo.title}</Text>
                          {!combo.isActive && <Tag>Не активно</Tag>}
                          <Tag color="blue">
                            {combo.pricingMode === 'fixed' ? `Фикс: ${combo.fixedPrice ?? 0} ₽` : 'Сумма блюд'}
                          </Tag>
                          <Tag color={combo.allowCustomization ? 'green' : 'default'}>
                            {combo.allowCustomization ? 'С выбором' : 'Без выбора'}
                          </Tag>
                        </Space>
                      }
                      description={
                        <Space direction="vertical" size={6}>
                          {combo.description ? <Text type="secondary">{combo.description}</Text> : null}
                          <Space wrap>
                            {(combo.groups || []).map((g: any) => (
                              <Tag key={g.id}>
                                {g.title}: {g.requiredCount} из {(g.items || []).length}
                              </Tag>
                            ))}
                          </Space>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            )}
          </Spin>

          <Divider />

          <Card size="small" title="Копирование на неделю">
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Space wrap>
                <Text strong>Источник</Text>
                <DatePicker value={comboCopySource} onChange={(d) => d && setComboCopySource(d)} />
                <Text strong>Куда</Text>
                <DatePicker.RangePicker value={comboCopyRange} onChange={(r) => setComboCopyRange(r as any)} />
              </Space>
              <Space wrap>
                <Radio.Group
                  value={comboCopyMode}
                  onChange={(e) => setComboCopyMode(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button value="replace">Заменить</Radio.Button>
                  <Radio.Button value="add">Добавить</Radio.Button>
                </Radio.Group>
                <Checkbox checked={comboCopyCombosOnly} onChange={(e) => setComboCopyCombosOnly(e.target.checked)}>
                  Копировать “только комбо”
                </Checkbox>
                <Button onClick={copyCombos}>Скопировать</Button>
              </Space>
            </Space>
          </Card>

          <Modal
            open={comboModalOpen}
            title={comboEditing?.id ? 'Редактировать комбо' : 'Создать комбо'}
            onCancel={() => {
              setComboModalOpen(false);
              setComboEditing(null);
            }}
            okText="Сохранить"
            cancelText="Отмена"
            onOk={saveCombo}
            width={isMobile ? '100%' : 760}
            destroyOnClose
          >
            {!comboEditing ? null : (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Input
                  placeholder="Название"
                  value={comboEditing.title}
                  onChange={(e) => setComboEditing((p) => (p ? { ...p, title: e.target.value } : p))}
                />
                <Input.TextArea
                  rows={3}
                  placeholder="Описание (опционально)"
                  value={comboEditing.description ?? ''}
                  onChange={(e) => setComboEditing((p) => (p ? { ...p, description: e.target.value || null } : p))}
                />
                <Space wrap>
                  <Checkbox
                    checked={comboEditing.allowCustomization}
                    onChange={(e) => setComboEditing((p) => (p ? { ...p, allowCustomization: e.target.checked } : p))}
                  >
                    Разрешить выбор внутри комбо
                  </Checkbox>
                  <Checkbox
                    checked={comboEditing.isActive}
                    onChange={(e) => setComboEditing((p) => (p ? { ...p, isActive: e.target.checked } : p))}
                  >
                    Активно
                  </Checkbox>
                </Space>

                <Space wrap align="center">
                  <Text strong>Цена</Text>
                  <Radio.Group
                    value={comboEditing.pricingMode}
                    onChange={(e) =>
                      setComboEditing((p) =>
                        p
                          ? {
                              ...p,
                              pricingMode: e.target.value,
                              fixedPrice: e.target.value === 'fixed' ? (p.fixedPrice ?? 0) : null
                            }
                          : p
                      )
                    }
                    optionType="button"
                    buttonStyle="solid"
                  >
                    <Radio.Button value="fixed">Фикс</Radio.Button>
                    <Radio.Button value="sum_items">Сумма блюд</Radio.Button>
                  </Radio.Group>
                  {comboEditing.pricingMode === 'fixed' && (
                    <InputNumber
                      min={0}
                      value={comboEditing.fixedPrice ?? 0}
                      onChange={(v) => setComboEditing((p) => (p ? { ...p, fixedPrice: (v as any) ?? 0 } : p))}
                      addonAfter="₽"
                    />
                  )}
                </Space>
                {comboEditing.pricingMode === 'fixed' ? (
                  <Alert
                    type="success"
                    showIcon
                    message="Режим «Фикс»: гость платит только указанную сумму набора"
                    description="Цены блюд в группах не влияют на итог. Включите «Разрешить выбор внутри комбо», чтобы гость выбирал варианты (А/Б)."
                  />
                ) : (
                  <Alert
                    type="warning"
                    showIcon
                    message="Режим «Сумма блюд»: итог = сумма выбранных позиций"
                    description="Для сета с одной ценой (например 349 ₽) лучше режим «Фикс»."
                  />
                )}

                <Divider />

                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Space wrap align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
                    <Text strong>Группы</Text>
                    <Button
                      icon={<PlusCircleOutlined />}
                      onClick={() =>
                        setComboEditing((p) =>
                          p
                            ? {
                                ...p,
                                groups: [
                                  ...p.groups,
                                  { title: `Группа ${p.groups.length + 1}`, requiredCount: 1, menuItemIds: [] }
                                ]
                              }
                            : p
                        )
                      }
                    >
                      Добавить группу
                    </Button>
                  </Space>

                  {comboEditing.groups.map((g, idx) => (
                    <Card
                      key={idx}
                      size="small"
                      title={`Группа ${idx + 1}`}
                      extra={
                        comboEditing.groups.length > 1 ? (
                          <Button
                            danger
                            type="link"
                            onClick={() =>
                              setComboEditing((p) =>
                                p ? { ...p, groups: p.groups.filter((_, i) => i !== idx) } : p
                              )
                            }
                          >
                            Удалить
                          </Button>
                        ) : null
                      }
                    >
                      <Space direction="vertical" size="small" style={{ width: '100%' }}>
                        <Input
                          value={g.title}
                          placeholder="Название группы"
                          onChange={(e) =>
                            setComboEditing((p) =>
                              p
                                ? {
                                    ...p,
                                    groups: p.groups.map((gg, i) => (i === idx ? { ...gg, title: e.target.value } : gg))
                                  }
                                : p
                            )
                          }
                        />
                        <Space wrap align="center">
                          <Text>Нужно выбрать</Text>
                          <InputNumber
                            min={1}
                            max={Math.max(1, g.menuItemIds.length)}
                            value={g.requiredCount}
                            onChange={(v) =>
                              setComboEditing((p) =>
                                p
                                  ? {
                                      ...p,
                                      groups: p.groups.map((gg, i) =>
                                        i === idx ? { ...gg, requiredCount: (v as any) ?? 1 } : gg
                                      )
                                    }
                                  : p
                              )
                            }
                          />
                          <Text type="secondary">из {g.menuItemIds.length || 0}</Text>
                        </Space>
                        <Select
                          mode="multiple"
                          style={{ width: '100%' }}
                          placeholder="Блюда в группе"
                          options={itemOptions}
                          value={g.menuItemIds}
                          onChange={(vals) =>
                            setComboEditing((p) =>
                              p
                                ? {
                                    ...p,
                                    groups: p.groups.map((gg, i) =>
                                      i === idx
                                        ? {
                                            ...gg,
                                            menuItemIds: vals as number[],
                                            requiredCount: Math.min(gg.requiredCount, (vals as number[]).length || 1)
                                          }
                                        : gg
                                    )
                                  }
                                : p
                            )
                          }
                        />
                      </Space>
                    </Card>
                  ))}
                </Space>
              </Space>
            )}
          </Modal>
        </Space>
      </Card>
    );
  };

  const modalPreviewSrc = resolveImageUrl(imageItem?.thumbnailUrl || imageItem?.imageUrl);
  const modalFullSrc = resolveImageUrl(imageItem?.imageUrl);

  return (
    <>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as DayPlannerTab)}
        items={[
          { key: 'catalog', label: 'Каталог блюд', children: renderCatalog() },
          { key: 'planner', label: 'Планирование меню', children: renderPlanner() },
          { key: 'combos', label: 'Комбо', children: renderCombos() }
        ]}
      />

      <Modal
        title={imageItem ? `Фотография блюда: ${imageItem.name}` : 'Фотография блюда'}
        open={imageModalVisible}
        onCancel={() => {
          if (!uploadingImage) {
            setImageModalVisible(false);
            setImageItem(null);
          }
        }}
        footer={null}
        destroyOnClose
        width={isMobile ? '100%' : 520}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          {modalPreviewSrc ? (
            <Image
              src={modalPreviewSrc}
              alt={imageItem?.name}
              style={{ borderRadius: 12, border: '1px solid #f0f0f0', width: '100%' }}
              preview={modalFullSrc ? { src: modalFullSrc } : false}
            />
          ) : (
            <Empty description="Фотография пока не загружена" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          )}

          <Upload.Dragger
            accept="image/jpeg,image/png,image/webp"
            multiple={false}
            maxCount={1}
            customRequest={handleImageUpload}
            showUploadList={false}
            disabled={uploadingImage}
            style={{ padding: 16 }}
          >
            <p className="ant-upload-drag-icon">
              <CloudUploadOutlined />
            </p>
            <p className="ant-upload-text">Перетащите файл или нажмите для выбора</p>
            <p className="ant-upload-hint">Поддерживаются изображения JPG, PNG, WebP до 10 МБ.</p>
          </Upload.Dragger>

          {(imageItem?.imageUrl || imageItem?.thumbnailUrl) && (
            <Button icon={<DeleteOutlined />} onClick={handleRemoveImage} danger loading={uploadingImage} block>
              Удалить фотографию
            </Button>
          )}
        </Space>
      </Modal>

      <Modal
        title="Создать новое блюдо"
        open={createVisible}
        onCancel={() => setCreateVisible(false)}
        onOk={createItem}
        okText="Создать"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Название" rules={[{ required: true }]}> 
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="category" label="Категория" rules={[{ required: true }]}> 
            <Select>
              <Select.Option value="soup">Суп</Select.Option>
              <Select.Option value="main_course">Основное блюдо</Select.Option>
              <Select.Option value="salad">Салат</Select.Option>
              <Select.Option value="bakery">Выпечка</Select.Option>
              <Select.Option value="dessert">Десерт</Select.Option>
              <Select.Option value="beverage">Напиток</Select.Option>
              <Select.Option value="snack">Закуска</Select.Option>
              <Select.Option value="breakfast">Завтраки</Select.Option>
              <Select.Option value="side_dish">Гарниры</Select.Option>
              <Select.Option value="other">Прочее</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="weight" label="Вес (г)" rules={[{ required: true }]}> 
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="calories" label="Калории" rules={[{ required: false }]}> 
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="price"
            label="Цена (₽)"
            rules={[{ required: true }]}
            extra="0 ₽ — только как вариант внутри комбо (в штучном меню сотрудникам не показывается)"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isAvailable" label="Доступно" initialValue={true}>
            <Select>
              <Select.Option value={true}>Да</Select.Option>
              <Select.Option value={false}>Нет</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Признаки блюда">
            <Space direction="vertical">
              <Form.Item name="isVegan" valuePropName="checked" noStyle>
                <Checkbox>🌱 Веганское</Checkbox>
              </Form.Item>
              <Form.Item name="isLenten" valuePropName="checked" noStyle>
                <Checkbox>⛪ Постное</Checkbox>
              </Form.Item>
              <Form.Item name="isHalal" valuePropName="checked" noStyle>
                <Checkbox>☪️ Халяль</Checkbox>
              </Form.Item>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Редактировать блюдо"
        open={editVisible}
        onCancel={() => setEditVisible(false)}
        onOk={editItem}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Название" rules={[{ required: true }]}> 
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Описание">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item name="category" label="Категория" rules={[{ required: true }]}> 
            <Select>
              <Select.Option value="soup">Суп</Select.Option>
              <Select.Option value="main_course">Основное блюдо</Select.Option>
              <Select.Option value="salad">Салат</Select.Option>
              <Select.Option value="bakery">Выпечка</Select.Option>
              <Select.Option value="dessert">Десерт</Select.Option>
              <Select.Option value="beverage">Напиток</Select.Option>
              <Select.Option value="snack">Закуска</Select.Option>
              <Select.Option value="breakfast">Завтраки</Select.Option>
              <Select.Option value="side_dish">Гарниры</Select.Option>
              <Select.Option value="other">Прочее</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item name="weight" label="Вес (г)" rules={[{ required: true }]}> 
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="calories" label="Калории" rules={[{ required: false }]}> 
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="price"
            label="Цена (₽)"
            rules={[{ required: true }]}
            extra="0 ₽ — только как вариант внутри комбо (в штучном меню сотрудникам не показывается)"
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="isAvailable" label="Доступно">
            <Select>
              <Select.Option value={true}>Да</Select.Option>
              <Select.Option value={false}>Нет</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item label="Признаки блюда">
            <Space direction="vertical">
              <Form.Item name="isVegan" valuePropName="checked" noStyle>
                <Checkbox>🌱 Веганское</Checkbox>
              </Form.Item>
              <Form.Item name="isLenten" valuePropName="checked" noStyle>
                <Checkbox>⛪ Постное</Checkbox>
              </Form.Item>
              <Form.Item name="isHalal" valuePropName="checked" noStyle>
                <Checkbox>☪️ Халяль</Checkbox>
              </Form.Item>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          plannerModalDate
            ? `Блюда на ${dayjs(plannerModalDate).format('DD.MM.YYYY')}`
            : 'Выбор блюд'
        }
        open={plannerModalVisible}
        onCancel={handlePlannerModalCancel}
        onOk={handlePlannerModalOk}
        okText="Сохранить"
        cancelText="Отмена"
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Select
            mode="multiple"
            showSearch
            value={plannerSelectedItems}
            onChange={(values) => setPlannerSelectedItems(values)}
            style={{ width: '100%' }}
            placeholder="Выберите блюда из каталога"
            optionLabelProp="label"
            filterOption={(input, option) =>
              ((option?.label as string) || '').toLowerCase().includes(input.toLowerCase())
            }
            options={items.map((item) => ({
              label: item.name,
              value: item.id
            }))}
          />
          <Alert
            type="info"
            showIcon
            message="Можно выбрать несколько блюд для выбранной даты. Чтобы убрать блюдо, снимите его в списке или воспользуйтесь кнопкой «Удалить» в карточке дня."
          />
        </Space>
      </Modal>
    </>
  );
};

export default MyMenu;

